<template>
  <div class="fields">
    <div class="field-row" v-for="(row, key) in rows" :key="key">
      <div
        v-for="(field, key) in row"
        class="field"
        :style="
          computedStyle({ id: field.id, category: field.category || category })
        "
        :key="
          field.id && configTemplate
            ? getFieldId({
                id: field.id,
                category: field.category || category,
              })
            : key
        "
      >
        <group
          v-if="field.type === 'group'"
          :category="field.category || category"
          :field="field"
          :parentField="parentField"
          :id="id"
          :hideLabel="hideLabel || field.configHideLabel"
          :isTemplate="isTemplate"
        />
        <div class="content" v-else-if="field.type === 'content'">
          <div
            class="body-2 content-field"
            :class="field.configNoMargin && 'no-margin'"
            v-html="field.content"
          />
          <tooltip v-if="field.tooltip" :text="field.tooltip" />
          <helpIcon v-if="field.helpContent" :content="field.helpContent" />
        </div>
        <contentGroup
          class="body-2 content-group"
          v-else-if="field.type === 'contentGroup'"
          :category="field.category || category"
          :field="field"
          :parentField="parentField"
          :id="id"
          :isTemplate="isTemplate"
          :tooltip="field.tooltip"
          :helpContent="field.helpContent"
        />
        <customFields
          v-else-if="field.type === 'custom'"
          :id="id"
          :category="field.category || category"
          :field="field.field"
          :value="
            getConfigValue({
              id: field.id,
              category: field.category || category,
            })
          "
          :isTemplate="isTemplate"
        />
        <configField
          v-else-if="
            getFieldData({ id: field.id, category: field.category || category })
          "
          :id="id"
          :category="field.category || category"
          :field="
            getFieldData({ id: field.id, category: field.category || category })
          "
          :value="
            getConfigValue({
              id: field.id,
              category: field.category || category,
            })
          "
          :layout="field.layout"
          :parentField="parentField"
          :hideLabel="hideLabel || field.configHideLabel"
          :class="field.configNoMargin && 'no-margin'"
          :isTemplate="isTemplate"
        />
      </div>
    </div>
  </div>
</template>

<script>
/**
 * @fileoverview Config fields
 *
 * @author Cameron Bulock <cameron.bulock@quavo.com>
 */

import { mapGetters } from "vuex";

import configField from "@/components/config-field";
import helpIcon from "@/components/help-icon";
import tooltip from "@/components/tooltip";

import contentGroup from "./content-group";
import customFields from "./custom-fields";
import group from "./group";

import getFieldDataInner from "@/util/getFieldData";

export default {
  name: "ConfigFields",
  components: {
    configField,
    contentGroup,
    customFields,
    group,
    helpIcon,
    tooltip,
  },
  props: {
    rows: {
      type: Array,
    },
    category: {
      type: String,
    },
    parentField: {
      type: String,
    },
    id: {
      type: String,
    },
    hideLabel: {
      type: Boolean,
    },
    isTemplate: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters({
      config: "getConfig",
      mergedConfig: "getMergedConfig",
      configTemplate: "getConfigTemplate",
    }),
  },
  methods: {
    computedStyle({ id, category }) {
      const fieldData = this.getFieldData({ id, category });

      const spacingValues = () => {
        // these values can be found in the qux-common components width calculations
        switch (fieldData && fieldData.Type) {
          case "dropdown":
            return {
              withIcon: 108,
              withoutIcon: 68,
            };
          default:
            return {
              withIcon: 65,
              withoutIcon: 25,
            };
        }
      };

      const spacing =
        fieldData && fieldData.Icon
          ? spacingValues.withIcon
          : spacingValues.withoutIcon;
      let computedStyle = "";
      if (fieldData && fieldData.Width) {
        computedStyle = `width: calc(${fieldData.Width} + ${spacing}px)`;
      } else {
        computedStyle = "width: 100%";
      }
      return computedStyle;
    },
    fieldList(category = this.category) {
      const fieldData = this.configTemplate && this.configTemplate[category];
      return fieldData && fieldData.AvailableFieldList;
    },
    getConfigValue({ id, category = this.category }) {
      if (this.parentField !== undefined) {
        return (
          this.mergedConfig[this.id] &&
          this.mergedConfig[this.id][category] &&
          this.mergedConfig[this.id][category][this.parentField] &&
          this.mergedConfig[this.id][category][this.parentField][id]
        );
      } else {
        return (
          this.mergedConfig[this.id] &&
          this.mergedConfig[this.id][category] &&
          this.mergedConfig[this.id][category][id]
        );
      }
    },
    getFieldData({ id, category }) {
      return getFieldDataInner(this.fieldList(category), id);
    },
    getFieldId({ id, category }) {
      const fieldData = this.getFieldData({
        id,
        category,
      });
      return (fieldData && fieldData["FieldId"]) || "";
    },
  },
};
</script>

<style lang="scss" scoped>
.fields {
  display: flex;
  flex-direction: column;
}
.field {
  &:not(:has(.content-group, .v-data-table, .show-border)) {
    max-width: 584px;
  }
}
.field-row {
  display: flex;
  gap: $qvo-spacing-24;
  &:not(:has(.content-group, .v-data-table, .show-border)) {
    max-width: 584px;
  }
}
.content {
  margin-bottom: $qvo-spacing-24;
  display: flex;
  align-items: center;
  gap: 12px;
  &:has(> .content-field > h6) {
    // whenever we have top-level content with <H6> headers, give a smaller margin
    margin-bottom: $qvo-spacing-8;
  }
  &:has(> .content-field.no-margin) {
    margin-bottom: 0;
  }
  &::v-deep {
    h6 {
      text-transform: uppercase;
    }
  }
}
.content-group {
  padding: $qvo-spacing-16 $qvo-spacing-16;
  margin-bottom: $qvo-spacing-24;
  border-radius: 8px;
  background: $qvo-color-blue-100;
  &::v-deep {
    .config-field {
      margin-bottom: 0;
    }
  }
}
</style>